import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { supabase } from "@/lib/supabase";
import {
  useSEOMeta,
  generateBreadcrumbSchema,
  generateWebPageSchema,
} from "@/components/SEO";

const categories = ["All", "Health", "Education", "Protection", "Economic Security", "General"];

export default function NewsPage() {
  const [allArticles, setAllArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useScrollAnimation();
  const [activeCategory, setActiveCategory] = useState("All");
  const location = useLocation();

  useSEOMeta({
    title: "News & Updates - BHODEV Cameroon | Latest Stories & Impact",
    description:
      "Stay informed with the latest news, projects, campaigns, and impact stories from BHODEV Cameroon. Read articles on health, education, economic security, and protection.",
    keywords:
      "BHODEV news, Cameroon NGO updates, humanitarian news Cameroon, community health stories, BHODEV articles, development news Cameroon, NGO campaigns Cameroon",
    canonicalUrl: "https://bhodevcameroon.org/news",
    ogImage:
      "https://readdy.ai/api/search-image?query=A%20journalist%20or%20reporter%20taking%20notes%20at%20a%20community%20event%20in%20Cameroon%2C%20warm%20golden%20hour%20lighting%2C%20people%20gathered%20in%20background%2C%20news%20and%20storytelling%20theme%2C%20wide%20landscape%2C%20photorealistic%20style&width=1200&height=630&seq=news-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "News & Updates", url: "https://bhodevcameroon.org/news" },
      ]),
      ...generateWebPageSchema(
        "News & Updates - BHODEV Cameroon",
        "Stay informed with the latest news, projects, campaigns, and impact stories from BHODEV Cameroon.",
        "https://bhodevcameroon.org/news"
      ),
    },
  });

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.pathname]);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .eq("status", "Published")
      .order("date", { ascending: false });
    if (error) {
      console.error("Error fetching articles:", error);
    } else {
      setAllArticles(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Re-observe scroll-animated elements after articles load
  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    const elements = document.querySelectorAll(".animate-on-scroll:not(.visible)");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [loading]);

  const featuredArticle = allArticles.find((a) => a.featured) || allArticles[0];
  const regularArticles = allArticles.filter((a) => a.id !== featuredArticle?.id);

  const filteredArticles =
    activeCategory === "All"
      ? regularArticles
      : regularArticles.filter((a) => a.category === activeCategory);

  const formatDate = (d: string) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] md:h-[520px] flex items-center justify-center overflow-hidden">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2F16-days-of-activism-for-the-fight-against-Gender-based-violence-in-North-west-and-South%2FIMG-20231209-WA0023.jpg?alt=media&token=bfa4ede6-6d05-4a17-a744-34a046cec2e3"
          alt="BHODEV News and Updates - Cameroon NGO Stories"
          title="BHODEV News and Updates - Cameroon NGO Stories"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <nav className="flex items-center justify-center gap-2 text-white/70 text-sm mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white font-semibold">News</span>
          </nav>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-saria font-bold text-white leading-tight mb-4">
            News &amp; Updates
          </h1>
          <p className="text-lg md:text-xl text-white/80 font-inter max-w-xl mx-auto leading-relaxed">
            Stay informed about our latest projects, campaigns, and impact stories from across Cameroon.
          </p>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && (
        <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <div className="animate-on-scroll">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-inter font-semibold mb-6 tracking-wide uppercase inline-block">
                Featured Story
              </span>
              <article className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center" itemScope itemType="https://schema.org/Article">
                <Link to={`/news/${featuredArticle.id}`} className="relative h-[300px] md:h-[400px] rounded-2xl overflow-hidden group cursor-pointer block">
                  <img
                    src={featuredArticle.image_url || "https://readdy.ai/api/search-image?query=Community%20development%20news%20and%20journalism%20in%20Cameroon%2C%20vibrant%20storytelling%20atmosphere%2C%20warm%20lighting%2C%20simple%20clean%20background%2C%20photorealistic%20style&width=800&height=500&seq=news-fallback&orientation=landscape"}
                    alt={featuredArticle.title}
                    title={featuredArticle.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    itemProp="image"
                  />
                  <div className="absolute top-5 left-5">
                    <span className="bg-white/90 backdrop-blur-sm text-primary text-xs px-3 py-1.5 rounded-full font-inter font-semibold shadow-sm">
                      {featuredArticle.category}
                    </span>
                  </div>
                </Link>
                <div>
                  <div className="flex items-center gap-3 mb-5" itemProp="author" itemScope itemType="https://schema.org/Person">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center border border-primary/20">
                      <span className="text-xs text-primary font-bold">{featuredArticle.author.split(" ").map((n: string) => n[0]).join("")}</span>
                    </div>
                    <div>
                      <span className="text-sm text-dark font-medium" itemProp="name">{featuredArticle.author}</span>
                      <p className="text-xs text-stone-400">
                        <time itemProp="datePublished" dateTime={featuredArticle.date}>
                          {formatDate(featuredArticle.date)}
                        </time>
                        &middot; {featuredArticle.read_time || "5 min read"}
                      </p>
                    </div>
                  </div>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-saria font-bold text-dark leading-tight mb-4 hover:text-primary transition-colors cursor-pointer" itemProp="headline">
                    {featuredArticle.title}
                  </h2>
                  <p className="text-stone-600 leading-relaxed mb-6 text-base md:text-lg" itemProp="description">
                    {featuredArticle.excerpt || ""}
                  </p>
                  <Link
                    to={`/news/${featuredArticle.id}`}
                    className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary/20"
                  >
                    Read Full Article
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-8 md:py-10 px-4 md:px-8 lg:px-12 bg-white border-b border-stone-100 sticky top-[60px] md:top-[72px] z-40">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-inter font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  activeCategory === cat
                    ? "bg-primary text-white shadow-md shadow-primary/20"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl md:text-3xl font-saria font-bold text-dark">
              {activeCategory === "All" ? "Latest Articles" : `${activeCategory} Articles`}
            </h2>
            <span className="text-sm text-stone-400 font-inter">
              {filteredArticles.length} {filteredArticles.length === 1 ? "article" : "articles"}
            </span>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
            </div>
          )}

          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredArticles.map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  className={`animate-on-scroll animate-on-scroll-delay-${(idx % 3) + 1} group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-500 border border-stone-100 block`}
                  itemScope
                  itemType="https://schema.org/Article"
                >
                  <div className="relative h-[220px] overflow-hidden">
                    <img
                      src={article.image_url || "https://readdy.ai/api/search-image?query=Community%20development%20news%20and%20journalism%20in%20Cameroon%2C%20vibrant%20storytelling%20atmosphere%2C%20warm%20lighting%2C%20simple%20clean%20background%2C%20photorealistic%20style&width=800&height=500&seq=news-fallback-2&orientation=landscape"}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      itemProp="image"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 backdrop-blur-sm text-primary text-xs px-3 py-1.5 rounded-full font-inter font-semibold shadow-sm">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4" itemProp="author" itemScope itemType="https://schema.org/Person">
                      <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center border border-primary/20">
                        <span className="text-xs text-primary font-bold">{article.author.split(" ").map((n: string) => n[0]).join("")}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-dark font-medium" itemProp="name">{article.author}</span>
                        <span className="text-xs text-stone-400">
                          <time itemProp="datePublished" dateTime={article.date}>
                            {formatDate(article.date)}
                          </time>
                          &middot; {article.read_time || "5 min read"}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-lg font-saria font-bold text-dark leading-snug mb-3 group-hover:text-primary transition-colors duration-300" itemProp="headline">
                      {article.title}
                    </h3>
                    <p className="text-sm text-stone-500 leading-relaxed mb-4 line-clamp-2" itemProp="description">
                      {article.excerpt || ""}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      <time className="text-xs text-stone-400" itemProp="datePublished" dateTime={article.date}>{formatDate(article.date)}</time>
                      <span className="flex items-center gap-1.5 text-primary font-semibold text-sm group-hover:gap-2.5 transition-all duration-300">
                        Read article
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && filteredArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
                <i className="ri-article-line text-2xl text-stone-400"></i>
              </div>
              <p className="text-stone-500 text-lg font-medium">No articles found in this category.</p>
              <button
                onClick={() => setActiveCategory("All")}
                className="mt-4 text-primary font-semibold hover:underline cursor-pointer"
              >
                View all articles
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-secondary">
        <div className="max-w-4xl mx-auto text-center animate-on-scroll">
          <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-2xl mx-auto mb-6">
            <i className="ri-mail-send-line text-2xl text-primary"></i>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-saria font-bold text-dark mb-4">
            Never Miss an Update
          </h2>
          <p className="text-stone-600 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            Subscribe to our newsletter and be the first to receive the Voice of Change — stories, updates, and opportunities straight to your inbox.
          </p>
          <form
            data-readdy-form
            id="newsletter-news-page"
            action="https://readdy.ai/api/form/d7o72i4jr1hvt8ntaukg"
            method="POST"
            className="flex flex-col sm:flex-row items-center gap-3 max-w-lg mx-auto"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              fetch(form.action, {
                method: "POST",
                body: new URLSearchParams(formData as never),
              }).then(() => {
                const btn = form.querySelector("button");
                if (btn) {
                  btn.textContent = "Subscribed!";
                  btn.classList.add("bg-green-600");
                }
              });
            }}
          >
            <div className="relative w-full">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line text-stone-400"></i>
              </div>
              <input
                type="email"
                name="email"
                placeholder="Enter your email address"
                required
                className="w-full bg-white border border-stone-200 rounded-xl pl-12 pr-4 py-3.5 text-sm focus:outline-none focus:border-primary text-dark placeholder-stone-400 transition-colors duration-300"
              />
            </div>
            <button
              type="submit"
              className="w-full sm:w-auto bg-primary text-white px-8 py-3.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary/20 shrink-0"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}